import React, { useCallback, useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { debounce } from "lodash";
import type { SourceRow, TopicRow } from "store/types/news";

import type { FeedConfigBuckets } from "../../context/NewsFeedContext";
import { GuidancePanel } from "./GuidancePanel";
import { SourcesPanel } from "./SourcesPanel";
import { TopicsPanel } from "./TopicsPanel";

type Props = {
  topics: FeedConfigBuckets<TopicRow>;
  sources: FeedConfigBuckets<SourceRow>;
  guidance: string;
  onSaveGuidance: (guidance: string) => Promise<void>;
  onCreateTopic: (payload: TopicRow) => Promise<TopicRow>;
  onUpdateTopic: (payload: TopicRow) => Promise<TopicRow>;
  onDeleteTopic: (id: number) => Promise<void>;
  onSelectSystemTopic: (id: number) => Promise<void>;
  onUnselectSystemTopic: (id: number) => Promise<void>;
  onCreateSource: (payload: SourceRow) => Promise<SourceRow>;
  onUpdateSourcePriority: (source_url: string, priority: number, name: string) => Promise<SourceRow>;
  onDeleteSource: (source_url: string) => Promise<void>;
  onSelectSystemSource: (source_url: string, name: string) => Promise<void>;
  onUnselectSystemSource: (source_url: string) => Promise<void>;
  onCancel: () => void;
};

type TabKey = "topics" | "sources" | "guidance";

const SAVE_GUIDANCE_DEBOUNCE_MS = 500;

export const ConfigureFeedDialog: React.FC<Props> = ({
  topics,
  sources,
  guidance,
  onSaveGuidance,
  onCreateTopic,
  onUpdateTopic,
  onDeleteTopic,
  onSelectSystemTopic,
  onUnselectSystemTopic,
  onCreateSource,
  onUpdateSourcePriority,
  onDeleteSource,
  onSelectSystemSource,
  onUnselectSystemSource,
  onCancel
}) => {
  const [tab, setTab] = useState<TabKey>("topics");
  const [guidanceDraft, setGuidanceDraft] = useState(guidance);

  const debouncedSaveGuidance = useMemo(
    () =>
      debounce((text: string) => {
        onSaveGuidance(text);
      }, SAVE_GUIDANCE_DEBOUNCE_MS),
    [onSaveGuidance]
  );

  const handleGuidanceChange = useCallback(
    (next: string) => {
      setGuidanceDraft(next);
      debouncedSaveGuidance(next);
    },
    [debouncedSaveGuidance]
  );

  useEffect(
    () => () => {
      debouncedSaveGuidance.flush();
    },
    [debouncedSaveGuidance]
  );

  const handleCloseClick = useCallback(() => {
    debouncedSaveGuidance.flush();
    onCancel();
  }, [debouncedSaveGuidance, onCancel]);

  return (
    <Box
      sx={{
        width: "min(720px, 94vw)",
        maxWidth: 720,
        minWidth: "min(720px, 94vw)",
        maxHeight: "min(720px, 90vh)",
        display: "flex",
        flexDirection: "column" as const,
        gap: "20px",
        p: "32px",
        boxSizing: "border-box" as const
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
          pb: "8px",
          borderBottom: "1px solid #DBE8F5",
          flexShrink: 0
        }}
      >
        <Typography component="div" sx={{ fontSize: "18px", fontWeight: 400, lineHeight: "24px", color: "#071F35" }}>
          Configure feed
        </Typography>
        <IconButton aria-label="close" onClick={handleCloseClick}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        TabIndicatorProps={{ sx: { display: "none" } }}
        sx={{
          minHeight: 0,
          flexShrink: 0,
          "& .MuiTabs-flexContainer": { gap: "16px", alignItems: "stretch" },
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "16px",
            color: "#496682",
            minHeight: 0,
            padding: "2px 0 6px",
            borderBottom: "2px solid transparent",
            minWidth: 0,
            "&.Mui-selected": { color: "#071F35", borderBottomColor: "#071F35" }
          }
        }}
      >
        <Tab label="Topics" value="topics" />
        <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "#DBE8F5", mb: "6px" }} />
        <Tab label="Sources" value="sources" />
        <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "#DBE8F5", mb: "6px" }} />
        <Tab label="Guidance" value="guidance" />
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Box sx={{ display: tab === "topics" ? "block" : "none" }}>
          <TopicsPanel
            initialTopics={topics}
            onCreateTopic={onCreateTopic}
            onUpdateTopic={onUpdateTopic}
            onDeleteTopic={onDeleteTopic}
            onSelectSystemTopic={onSelectSystemTopic}
            onUnselectSystemTopic={onUnselectSystemTopic}
          />
        </Box>
        <Box sx={{ display: tab === "sources" ? "block" : "none" }}>
          <SourcesPanel
            initialSources={sources}
            onCreateSource={onCreateSource}
            onUpdateSourcePriority={onUpdateSourcePriority}
            onDeleteSource={onDeleteSource}
            onSelectSystemSource={onSelectSystemSource}
            onUnselectSystemSource={onUnselectSystemSource}
          />
        </Box>
        <Box sx={{ display: tab === "guidance" ? "block" : "none" }}>
          <GuidancePanel value={guidanceDraft} onChange={handleGuidanceChange} />
        </Box>
      </Box>
    </Box>
  );
};
